import React, { useState } from 'react';

import { useBaseWidget } from 'hooks/useBaseWidget';
import { useQueryState } from 'hooks/useQueryState';

import {
    TDateRangeOption,
    TableDateRangeFilter,
} from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { List } from 'components/common/ListWrapper/list';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { MyCustomSelect } from 'components/common';
import { useRiskDensityLogic } from 'components/widgets/RiskDensity/RiskDensity.logic';
import classes from 'components/widgets/RiskDensity/RiskDensity.module.scss';
import { useRiskDensityProvider } from 'components/widgets/RiskDensity/RiskDensity.provider';
import {
    SelectByOptionsValue,
    resolutionOptions,
    selectByOptions,
} from 'components/widgets/RiskDensity/RiskDensity.utils';
import CriticalAnalysisHeatMap from 'components/widgets/RiskDensity/components/CriticalAnalysisHeatMap/CriticalAnalysisHeatMap';

const title = 'Risk Density by';
const id = 'RiskDensityByActivityStarts';
const componentKey = 'riskHeatMap';

const RiskDensity = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    // state
    const [startProjectDate, setStartProjectDate] = useState<number | null>(null);
    const [endProjectDate, setEndProjectDate] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState<{ startDate: number | null; endDate: number | null }>({
        startDate: null,
        endDate: null,
    });
    const [dateRangeOption, setDateRangeOption] = useState<TDateRangeOption>('wholeProject');
    const [resolution, setResolution] = useState<boolean>(false);
    const [riskDensityBy, setRiskDensityBy] = useQueryState(
        'riskDensityBy',
        widgetId ? undefined : 'heatMapActivityStarts',
    );
    const [selectBy, setSelectBy] = useState<SelectByOptionsValue | null>(
        riskDensityBy ? selectByOptions.find((filter) => filter.value === riskDensityBy)?.value || null : null,
    );

    // provider
    const { ui, api } = useRiskDensityProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        dateRange,
        resolution,
        selectBy,
        startProjectDate,
        endProjectDate,
        dateRangeOption,
        riskDensityBy,
        componentKey,
    });

    // logic
    const {
        handleDateRangeSelection,
        handleResolutionFilter,
        handleSelectBy,
        serialized,
        setDateOnDelayDriver,
        selectByValue,
        resolutionValue,
    } = useRiskDensityLogic({
        ui,
        api,
        setStartProjectDate,
        setEndProjectDate,
        setDateRange,
        setDateRangeOption,
        setRiskDensityBy,
        setResolution,
        setSelectBy,
    });

    // baseWidget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={title}
            tooltip={api.riskDensityBy.data?.info}
            titleComponents={[
                <div className={classes.titleComponents} key={'filterCriticalAnalysisHeatMap'}>
                    {ui.isInPinboard ? (
                        <h3>{selectByValue?.label}</h3>
                    ) : (
                        <>
                            <MyCustomSelect<ISelectOption<SelectByOptionsValue>>
                                id={'filterCriticalAnalysisHeatMap'}
                                value={selectByValue}
                                options={selectByOptions}
                                onChange={handleSelectBy}
                                key={'filterCriticalAnalysisHeatMap-Key'}
                            />
                            <div className={classes.dateRangeWrapper}>
                                <div>{FilterLabels.dateRange}</div>
                                {ui.state.startProjectDate && ui.state.endProjectDate && (
                                    <TableDateRangeFilter
                                        onChange={handleDateRangeSelection}
                                        minDate={ui.state.startProjectDate}
                                        maxDate={ui.state.endProjectDate}
                                        value={ui.state.dateRange}
                                        isDisabled={ui.isInPinboard}
                                        dateRangeOption={ui.state.dateRangeOption}
                                        setDateRangeOption={setDateRangeOption}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>,
                !ui.isInPinboard && (
                    <div className={classes.resolutionWrapper} key={'resolution'}>
                        <div className={classes.title}>Resolution:</div>
                        <List<boolean>
                            onChange={handleResolutionFilter}
                            options={resolutionOptions}
                            value={resolutionValue}
                        />
                    </div>
                ),
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    title={title}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,

                widgetId && ui.isInPinboard && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : null,
            ]}
        >
            <div className={classes.topSection}>
                <div>{!ui.isInPinboard && <span>Click any month to filter the activities table below.</span>}</div>
            </div>
            <CriticalAnalysisHeatMap
                data={serialized}
                handleClick={setDateOnDelayDriver}
                resolution={ui.state.resolution}
                noData={!api.riskDensityBy.isFetching && api.riskDensityBy.data !== null && serialized.length === 0}
                isFetching={api.riskDensityBy.isFetching}
            />
        </WidgetWithTitle>
    );
};

export default RiskDensity;
